import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import ConfigService, { Config } from "@shared/helpers/configServices"
import { defineStore } from "pinia"
import { computed, ref, watch } from "vue"

export interface Settings {
  isSidebarMinimized: boolean
  isLoading: boolean
  isAppLoading?: boolean
  configurations: Config
}

export interface UserPreferences {
  selectedTheme?: string
  menuFavorites?: string
  dashboardWidget?: string
}

const SESSION_KEY = "JTP_global_settings"

export const useGlobalStore = defineStore("global", () => {
  const stored = sessionStorage.getItem(SESSION_KEY)

  const settings = ref<Settings>(
    stored
      ? JSON.parse(stored)
      : {
          isSidebarMinimized: false,
          isLoading: false,
          isAppLoading: true,
        },
  )
  settings.value.configurations = ConfigService.Current

  const userPreferences = ref<UserPreferences>({
    selectedTheme: "light",
    menuFavorites: "",
  })
  const menuHierarchy = ref<MenuHierarchy[]>([])

  // Persist to sessionStorage on change
  watch(
    settings,
    (val) => {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(val))
    },
    { deep: true },
  )

  //Getters
  const isSidebarMinimized = computed(() => !!settings.value.isSidebarMinimized)

  //Actions
  function toggleSidebar() {
    settings.value.isSidebarMinimized = !settings.value.isSidebarMinimized
  }

  function setTheme(theme: string) {
    userPreferences.value.selectedTheme = theme
  }

  function setFavorites(favorites: string) {
    if (userPreferences.value.menuFavorites !== favorites) {
      userPreferences.value.menuFavorites = favorites
    }
  }

  function setDashboardWidget(dashboardWidget: string) {
    userPreferences.value.dashboardWidget = dashboardWidget
  }

  function setUserPreferences(preferences: UserPreferences) {
    userPreferences.value = preferences
  }

  function showLoading() {
    settings.value.isLoading = true
  }

  function hideLoading() {
    settings.value.isLoading = false
  }

  function showAppLoading() {
    settings.value.isAppLoading = true
  }

  function hideAppLoading() {
    settings.value.isAppLoading = false
  }

  function setMenuHierarchy(menu: MenuHierarchy[]) {
    menuHierarchy.value = menu
  }

  return {
    settings,
    userPreferences,
    setUserPreferences,
    menuHierarchy,
    setMenuHierarchy,
    setFavorites,
    isSidebarMinimized,
    toggleSidebar,
    setTheme,
    setDashboardWidget,
    showLoading,
    hideLoading,
    showAppLoading,
    hideAppLoading,
  }
})
